import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Gift, ArrowRight, Users, Building2, Heart, Sparkles } from "lucide-react";
import { useCopy } from "@/lib/copyVariant";

const CDN = "https://cdn.prod.website-files.com/5e95b7d352d2e8c65a55a3b9/";
const CARD_FRONT = `${CDN}65cd0547a7425e9854fd9ac9_Joker%206.jpg`;
const CARD_BACK = `${CDN}65cd02ffceed6ae8e6262ddd_A%20heart.jpg`;
const ORB = "https://customer-assets.emergentagent.com/job_calma-sutras/artifacts/kvoym993_65cc832cacc7ac557a389a4c_orb.avif";

const TILES = [
  { id: "employees", title: "Your team", body: "Wellness, onboarding, anniversaries.", Icon: Users },
  { id: "customers", title: "Clients & vendors", body: "Thank the people who chose you.", Icon: Building2 },
  { id: "personal", title: "Someone you love", body: "A daily practice of calm, sent with intention.", Icon: Heart },
];

export default function GiftLandingSection() {
  const { c } = useCopy();
  return (
    <section
      id="gift"
      className="relative py-24 sm:py-32 px-5 md:px-10 overflow-hidden"
      style={{ background: "linear-gradient(180deg, #fff8f3 0%, #f5ede4 60%, #fff8f3 100%)" }}
      data-testid="gift-landing-section"
    >
      {/* Decorative orb - top right */}
      <motion.img
        src={ORB} alt=""
        aria-hidden="true"
        loading="lazy" decoding="async" width="520" height="520"
        initial={{ opacity: 0, scale: 0.8 }}
        whileInView={{ opacity: 0.45, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.2 }}
        className="pointer-events-none absolute -top-20 -right-20 w-[420px] h-[420px] sm:w-[520px] sm:h-[520px] blur-[1px] opacity-40"
      />

      {/* Section eyebrow */}
      <div className="max-w-[1200px] mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.6 }}
          className="text-center mb-12 sm:mb-16"
        >
          <div className="inline-flex items-center gap-2 bg-[#a6c1a8]/25 text-[#587e62] rounded-full px-4 py-1.5 mb-5 backdrop-blur">
            <Sparkles size={13} />
            <span className="text-[11px] uppercase tracking-[0.3em] font-semibold">{c("giftSectionEyebrow")}</span>
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl font-bold text-[#464646] leading-[1.05] tracking-tight max-w-4xl mx-auto">
            {c("giftSectionHeadlineL1")}<br/>
            <span className="italic text-[#587e62]">{c("giftSectionHeadlineL2")}</span>
          </h2>
          <p className="mt-6 text-base sm:text-lg text-[#464646]/70 max-w-2xl mx-auto leading-relaxed">
            {c("giftSectionBody")}
          </p>
        </motion.div>

        {/* Two-column visual + tiles */}
        <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-16 items-center mb-14">
          {/* Two stacked cards - card-style with rounded corners & border */}
          <motion.div
            initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.9 }}
            className="relative flex items-center justify-center min-h-[360px]"
            data-testid="gift-landing-visual"
          >
            {/* Back card (slightly rotated, peeking behind) */}
            <motion.div
              initial={{ opacity: 0, rotate: -18, x: -20 }}
              whileInView={{ opacity: 1, rotate: -10, x: -56 }}
              viewport={{ once: true }} transition={{ duration: 1.1, delay: 0.2 }}
              className="absolute w-[180px] sm:w-[200px] aspect-[5/7] rounded-2xl overflow-hidden border-[3px] border-white/90 bg-white"
              style={{ boxShadow: "0 14px 40px rgba(70,70,70,0.18)" }}
            >
              <img src={CARD_BACK} alt="" aria-hidden="true" loading="lazy" decoding="async" width="220" height="308" className="w-full h-full object-cover" />
            </motion.div>
            {/* Front card */}
            <motion.div
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              className="relative z-10 w-[210px] sm:w-[240px] aspect-[5/7] rounded-2xl overflow-hidden border-[4px] border-white bg-white"
              style={{ boxShadow: "0 22px 50px rgba(70,70,70,0.22)" }}
            >
              <img src={CARD_FRONT} alt="A Calmasutras card" loading="lazy" decoding="async" width="220" height="308" className="w-full h-full object-cover" />
            </motion.div>

            {/* Floating affirmation chips */}
            <motion.div
              initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }} transition={{ delay: 0.7, duration: 0.6 }}
              className="absolute top-6 left-0 sm:left-2 bg-white/95 backdrop-blur rounded-2xl px-3 py-2 shadow-xl border border-[#464646]/10 hidden sm:block z-20"
            >
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#96aeb4] font-semibold">52 cards</p>
              <p className="text-sm font-semibold text-[#464646]">52 moments of calm</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }} transition={{ delay: 1, duration: 0.6 }}
              className="absolute bottom-8 right-0 sm:right-2 bg-white/95 backdrop-blur rounded-2xl px-3 py-2 shadow-xl border border-[#464646]/10 hidden sm:block z-20"
            >
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#587e62] font-semibold">From $25 / deck</p>
              <p className="text-sm font-semibold text-[#464646]">Bulk pricing built in</p>
            </motion.div>
          </motion.div>

          {/* Tiles + CTA */}
          <div className="space-y-6">
            <p className="text-[11px] uppercase tracking-[0.3em] text-[#96aeb4] font-semibold">Gift it to</p>
            <div className="space-y-3">
              {TILES.map((t, i) => (
                <motion.div
                  key={t.id}
                  initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }} transition={{ delay: i * 0.15, duration: 0.55 }}
                >
                  <Link
                    to={`/gift?for=${t.id}`}
                    className="group flex items-center gap-4 rounded-2xl bg-white/80 backdrop-blur border border-[#464646]/10 hover:border-[#587e62]/40 hover:bg-white p-4 sm:p-5 transition-all hover:shadow-lg"
                    data-testid={`gift-landing-tile-${t.id}`}
                  >
                    <span className="w-11 h-11 rounded-full bg-gradient-to-br from-[#a6c1a8] to-[#587e62] flex items-center justify-center shrink-0 shadow-md">
                      <t.Icon size={16} className="text-white" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-[#464646] text-base">{t.title}</p>
                      <p className="text-sm text-[#464646]/60 truncate">{t.body}</p>
                    </div>
                    <ArrowRight size={16} className="text-[#464646]/30 group-hover:text-[#587e62] group-hover:translate-x-1 transition-all shrink-0" />
                  </Link>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: 0.6 }}
              className="pt-2"
            >
              <Link
                to="/gift"
                className="inline-flex items-center gap-2 bg-[#464646] hover:bg-[#587e62] text-white rounded-full px-7 py-3.5 text-sm font-semibold transition-colors shadow-lg shadow-[#464646]/15 hover:shadow-xl hover:shadow-[#587e62]/25"
                data-testid="gift-landing-cta"
              >
                <Gift size={15} /> Start a gift order <ArrowRight size={14} />
              </Link>
              <p className="text-[11px] text-[#464646]/55 mt-3">
                Free shipping at 10+ decks · Ships worldwide · One private link sent to your recipients
              </p>
            </motion.div>
          </div>
        </div>

        {/* Closing line */}
        <motion.p
          initial={{ opacity: 0 }} whileInView={{ opacity: 1 }}
          viewport={{ once: true }} transition={{ delay: 0.3, duration: 0.8 }}
          className="text-center font-serif italic text-lg sm:text-xl text-[#464646]/65 max-w-2xl mx-auto"
        >
          "To gift calm is to give back time. To give back time is the rarest gift of all."
        </motion.p>
      </div>
    </section>
  );
}
